import {useEffect, useRef, useState} from 'react'
import {VideoPlayer} from '@primer/react-brand'

import type {Asset} from '@github-ui/swp-core/schemas/contentful/contentTypes/asset'
import styles from './VideoHosted.module.css'

type VideoHostedProps = {
  optionVideo: Asset
  optionPoster?: Asset
  autoplay?: boolean
  priority?: 'high'
}

/**
 * Hosted/uploaded video rendered via Primer's VideoPlayer.
 *
 * When `autoplay` is true the video starts muted with only a
 * play/pause toggle visible (no seek bar, CC, volume, etc.).
 *
 * Re-dispatches `loadedmetadata` when the browser loads video metadata
 * before VideoProvider's useEffect attaches its listener (common with
 * CDN-hosted videos).
 */
export function VideoHosted({optionVideo, optionPoster, autoplay, priority}: VideoHostedProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  // This prevents the video from playing before the surrounding context displays.
  const [canAutoplay, setCanAutoplay] = useState(
    // eslint-disable-next-line @github-ui/github-monorepo/no-stale-read-in-render
    () => !autoplay || (typeof document !== 'undefined' && document.readyState === 'complete'),
  )

  useEffect(() => {
    if (!autoplay || canAutoplay) {
      return
    }

    const handleLoad = () => setCanAutoplay(true)
    window.addEventListener('load', handleLoad, {once: true})

    return () => {
      window.removeEventListener('load', handleLoad)
    }
  }, [autoplay, canAutoplay])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    if (video.readyState >= 1) {
      video.dispatchEvent(new Event('loadedmetadata'))
    }
    // When autoPlay is used, the browser may fire `play` before
    // VideoProvider's useEffect attaches its listener. Re-dispatch
    // so the internal isPlaying state syncs correctly.
    if (!video.paused) {
      video.dispatchEvent(new Event('play'))
    }
  }, [])

  return (
    <>
      {priority === 'high' && optionPoster && (
        <link rel="preload" as="image" href={optionPoster.fields.file.url} fetchPriority="high" />
      )}
      <div className={autoplay ? styles.autoplayed : undefined}>
        <VideoPlayer
          visuallyHiddenTitle
          ref={videoRef}
          title={optionVideo.fields.description ?? 'Video'}
          poster={optionPoster?.fields.file.url}
          {...(autoplay && canAutoplay && {autoPlay: true, muted: true, playsInline: true})}
          showSeekControl={!autoplay}
          showCCButton={!autoplay}
          showMuteButton={!autoplay}
          showVolumeControl={!autoplay}
          showFullScreenButton={!autoplay}
          showBranding={false}
        >
          <VideoPlayer.Source src={optionVideo.fields.file.url} type="video/mp4" />
        </VideoPlayer>
      </div>
    </>
  )
}
